import dayjs from 'dayjs';
import { toastConfig } from 'config';
import utc from 'dayjs/plugin/utc.js';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import SectionCard from 'components/section/SectionCard';
import { groupBy, isEqual, map, mapValues } from 'lodash';
import { Col, Form, Input, Row, Select, Spin } from 'antd';
import AddIconButton from 'components/button/AddIconButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { filterChildOptionLabel } from 'pages/globals/global';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import CustomDateInput from 'components/inputs/CustomDateInput';
import DetailPageHeader from 'components/header/DetailPageHeader';
import DeleteIconButton from 'components/button/DeleteIconButton';
import FormSubmitButton from 'components/button/FormSubmitButton';
import CustomSelectInput from 'components/inputs/CustomSelectInput';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetLynFormDataMutation } from 'store/features/comptroller/comptrollerApi';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
	useGetSingleIdentityMutation,
	useGetLynFormPropertyMutation,
	useGetCheckAllUserCheckByIdentitiesMutation,
} from 'store/features/process/processApi';

dayjs.extend(utc);

export const queryNameMap = {
	'PROPERTY_Identity-Opt_band': 'Bands',
	'PROPERTY_Identity-Opt_position': 'leavePolicyPositions',
	'PROPERTY_Identity-Opt_function': 'leavePolicyFunctions',
	'PROPERTY_Identity-Opt_userId': 'leavePolicyUsers',
};

export const leaveTypeOptions = [
	{ key: 1, label: 'Annual Leave', value: 'annual' },
	{ key: 2, label: 'Sick Leave', value: 'sick' },
	{ key: 3, label: 'Casual Leave', value: 'casual' },
	{ key: 4, label: 'Maternity', value: 'maternity' },
	{ key: 5, label: 'Paternity', value: 'paternity' },
	{ key: 6, label: 'Religious', value: 'religious' },
	{ key: 7, label: 'Study', value: 'study' },
	{ key: 8, label: 'Compensatory', value: 'compensatory' },
	{ key: 9, label: 'Carryovers', value: 'carryovers' },
	{ key: 10, label: 'Leave Encashment', value: 'leaveEncashment' },
];

export const leaveCategoryOptions = [
	{
		key: 1,
		label: 'Regular Leave',
		value: 'regular',
		connectOptions: [
			{ key: 1, label: 'Annual Leave', value: 'annual' },
			{ key: 2, label: 'Sick Leave', value: 'sick' },
			{ key: 3, label: 'Casual Leave', value: 'casual' },
		],
	},
	{
		key: 2,
		label: 'Special Leave',
		value: 'special',
		connectOptions: [
			{ key: 4, label: 'Maternity', value: 'maternity' },
			{ key: 5, label: 'Paternity', value: 'paternity' },
			{ key: 6, label: 'Religious', value: 'religious' },
			{ key: 7, label: 'Study', value: 'study' },
			{ key: 8, label: 'Compensatory', value: 'compensatory' },
		],
	},
	{
		key: 3,
		label: 'Leave Governance',
		value: 'governance',
		connectOptions: [
			{ key: 9, label: 'Carryovers', value: 'carryovers' },
			{ key: 10, label: 'Leave Encashment', value: 'leaveEncashment' },
			{ key: 11, label: 'Probation Period', value: 'probationPeriod' },
			{ key: 12, label: 'Leave Accrual', value: 'leaveAccrual' },
		],
	},
];

function groupAndExtractIdentities(data) {
	const groupedByContext = groupBy(data, 'identityContext');

	const result = mapValues(groupedByContext, (group) => map(group, 'identityValue'));

	return result;
}

const transformPolicyData = (policyData) => {
	const result = {
		users: [],
		functions: [],
		positions: [],
		jobBands: [],
	};

	const policyTable = policyData?.policyTable;

	if (!Array.isArray(policyTable)) {
		return result;
	}

	const modifiedResults = groupAndExtractIdentities(policyTable);

	return modifiedResults ?? result;
};

function renameKeyMutable(data) {
	if (Object.prototype.hasOwnProperty.call(data, 'band')) {
		data.jobBands = data.band || [];
		data.users = data.userId || [];
		data.functions = data.function || [];
		data.positions = data.position || [];

		delete data.band;
		delete data.userId;
		delete data.function;
		delete data.position;
	}
	return data;
}

function capitalizeFirstLetter(str) {
	if (!str || typeof str !== 'string') {
		return '';
	}
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function toTitleCase(str) {
	if (!str || typeof str !== 'string') {
		return '';
	}

	return str
		.split(' ') // 1. Split the string into an array of words
		.map((word) => capitalizeFirstLetter(word)) // 2. Capitalize each word in the array
		.join(' '); // 3. Join the words back into a single string
}

function Update({ screen = 'VIEW' }) {
	const [form] = Form.useForm();
	const [isFormChanged, setIsFormChanged] = useState(false);
	const initialValuesRef = useRef(null);
	const { state } = useLocation();
	// console.log(state, 'state123');
	const navigate = useNavigate();
	const [showModal, setShowModal] = useState(false);
	const [submitData, setSubmitData] = useState();
	const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
	const [filteredLeaveTypes, setFilteredLeaveTypes] = useState([]);
	const selectedCategory = Form.useWatch('policyCategory', form);
	const [AddSingleIdentity, { isLoading: isSingleCaseLoading, isError: isCreateError, error: createError, isSuccess: isCreateSuccess }] =
		useGetSingleIdentityMutation();
	const [
		GetSingleIdentity,
		{ data: getSingleData, isLoading: isGetSingleCaseLoading, isError: isGetSingleError, error: getSingleError, isSuccess: isGetSingleSuccess },
	] = useGetSingleIdentityMutation();
	const [statusChecker] = useGetCheckAllUserCheckByIdentitiesMutation();
	const handleCategoryChange = (value) => {
		// Find the options for the newly selected category
		const categoryData = leaveCategoryOptions.find((cat) => cat.value === value);
		setFilteredLeaveTypes(categoryData?.connectOptions || []);

		// Explicitly reset the dependent fields ONLY when the user changes the category
		form.setFieldsValue({
			leaveType: undefined,
			// Also reset the recurrence in the table
			policyTable: (form.getFieldValue('policyTable') || []).map((row) => ({
				...row,
				recurrence: undefined,
			})),
		});
	};
	const onSubmitHandler = async (payload) => {
		// console.log(payload, 'payload5566');
		const { effectiveDateTime, ...rest } = payload;
		const transformedTable = payload.policyTable
			?.map((row) => {
				// Skip any empty/invalid rows that might be in the form state
				if (!row || !row['PROPERTY_Identity-Opt']) {
					return null;
				}

				// Extract the last part of the 'PROPERTY_Identity-Opt' string (e.g., 'position')
				const identityContext = row['PROPERTY_Identity-Opt'].split('_').pop();

				// Destructure to remove transient UI fields like identityLabel and valueLabel
				const { identityLabel, valueLabel, ...restOfRow } = row;

				// Return a new row object with the added 'identityContext' field
				return {
					...restOfRow,
					identityContext: identityContext,
				};
			})
			.filter(Boolean); // Filter out any null values from invalid rows

		// Construct the final payload with the transformed table
		const modifiedPayload = {
			// effectiveDateTime: getPreviousMonthLastDatePlus18GMT(effectiveDateTime),
			effectiveDateTime: effectiveDateTime,
			...rest,
			policyTable: transformedTable,
		};

		const policyDataCheck = renameKeyMutable(transformPolicyData(modifiedPayload));
		const checkResult = await statusChecker(policyDataCheck);

		setSubmitData(modifiedPayload);
		setShowModal(true);
	};

	const onModalOkHandler = () => {
		AddSingleIdentity({
			operation: 'add-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: submitData,
		});
	};

	useEffect(() => {
		if (isCreateSuccess) {
			toast.success('Success! Action completed ', toastConfig);
			navigate(-1);
		}
		if (isCreateError) {
			if (createError?.data?.code === '501') {
				toast.error(createError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isCreateSuccess, createError, isCreateError]);

	useEffect(() => {
		if (state?._id && isGetSingleSuccess) {
			form.setFieldsValue({
				policyCategory: getSingleData?.parkedData?.policyCategory,
				leaveType: getSingleData?.parkedData?.leaveType,
				effectiveDateTime: getSingleData?.parkedData?.effectiveDateTime ? dayjs(getSingleData.parkedData.effectiveDateTime) : null,
				policyTable: getSingleData?.parkedData?.policyTable,
			});
		}
	}, [state, isGetSingleSuccess]);

	useEffect(() => {
		if (state?.processCode && state?._id) {
			GetSingleIdentity({
				_id: state._id,
				operation: 'get-single',
				processCode: state?.processCode,
				suffixCode: '',
			});
		} else if (state?.data) {
			// Step 1: Find the dependent options for the incoming category
			const initialCategory = state.data.policyCategory;
			const categoryData = leaveCategoryOptions.find((cat) => cat.value === initialCategory);

			// Step 2: Set the state for the dependent dropdown
			if (categoryData) {
				setFilteredLeaveTypes(categoryData.connectOptions || []);
			}

			// Step 3: Set ALL form values at once.
			// This now works because `filteredLeaveTypes` is populated,
			// and we are not triggering the second hook to reset anything.
			const formData = {
				policyCategory: state.data.policyCategory,
				leaveType: state.data.leaveType,
				// policyName: state.data.policyName,
				// effectiveDateTime: dayjs(state.data.effectiveDateTime),
				policyTable: state.data.policyTable,
			};
			initialValuesRef.current = formData;
			form.setFieldsValue(formData);
		} else {
			navigate(-1);
		}
	}, [state?._id]);

	return (
		<Box>
			<DetailPageHeader title={`Update ${toTitleCase(state?.data?.leaveType)} Leave Policy`} />

			<Spin spinning={isSingleCaseLoading} style={{ maxHeight: '100%' }}>
				<SectionCard>
					<Form
						form={form}
						id="policy-create"
						layout="vertical"
						onValuesChange={(changedValues, allValues) => {
							if (JSON.stringify(state.data.policyTable) !== JSON.stringify(allValues?.policyTable)) {
								setIsSubmitDisabled(false);
							} else {
								setIsSubmitDisabled(true);
							}

							// Use lodash's isEqual for a deep comparison of objects/arrays
							if (!isEqual(initialValuesRef.current, allValues)) {
								setIsFormChanged(true);
							} else {
								setIsFormChanged(false);
							}
						}}
						onFinish={onSubmitHandler}
					>
						<AntdValuePreview form={form} show={true} />

						<Row gutter={[16, 0]}>
							<Col xs={24} sm={24} md={24} xl={24}>
								<CustomSelectInput
									label="Category"
									name="policyCategory"
									disabled
									onChange={handleCategoryChange}
									rules={[
										{
											required: true,
											message: 'Leave category is required',
										},
									]}
								>
									{leaveCategoryOptions.map((item, index) => (
										<Select.Option key={index} value={item?.value}>
											{item?.label}
										</Select.Option>
									))}
								</CustomSelectInput>
							</Col>

							<Col xs={24} sm={24} md={24} xl={24}>
								<CustomSelectInput
									label="Leave Type"
									name="leaveType"
									disabled
									rules={[
										{
											required: true,
											message: 'Leave type is required',
										},
									]}
								>
									{filteredLeaveTypes.map((item, index) => (
										<Select.Option key={index} value={item?.value}>
											{item?.label}
										</Select.Option>
									))}
								</CustomSelectInput>
							</Col>

							<Col xs={24} sm={24} md={24} xl={24}>
								<CustomDateInput
									label="Effective Date"
									form={form}
									name="effectiveDateTime"
									rules={[
										{
											required: true,
											message: 'Effective date field is required',
										},
									]}
									disabled={screen === 'VIEW' ? true : false}
								/>
							</Col>

							<Col xs={24}>
								<Form.List name="policyTable">
									{(fields, { add, remove }) => (
										<TableContainer
											className="render-condition"
											sx={{
												mb: 3,
												'& table, & th, & td': {
													border: '1px solid #e0e0e0',
													borderCollapse: 'collapse',
												},
												'& th': {
													py: '5px',
													px: '11px',
												},
												'& td': {
													p: '5px',
												},
												'& td .ant-form-item': {
													marginBottom: '0px',
												},
												'& .condition-msg': {
													py: 3,
												},
												overflow: 'hidden',
												'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' } /* From/From Month */,
												'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' } /* To/To Month */,
												'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' } /* Amount/Monthly Adjustment */,
												'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
												'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' } /* Action */,
												'& table': { width: '100%' } /* Ensure table takes full container width */,
											}}
										>
											<Table
												sx={{
													'& .MuiTableBody-root .MuiTableCell-root': {
														p: 0.5,
													},
													'& .ant-form-item': {
														marginBottom: 0,
													},
												}}
											>
												<TableHead>
													<TableRow sx={{ backgroundColor: '#e5e5e5' }}>
														<TableCell align="center">Identity</TableCell>
														<TableCell align="center">Value</TableCell>
														<TableCell align="center">Entitlement</TableCell>
														<TableCell>{`${selectedCategory === 'regular' ? 'Accrual' : 'Recurrence'}`}</TableCell>
														{screen !== 'VIEW' && <TableCell align="center">Actions</TableCell>}
													</TableRow>
												</TableHead>
												<TableBody>
													{fields?.map((field, index) => (
														<TableRow key={index}>
															<TableCell>
																<PolicyTableSelect
																	name={[field.name, 'PROPERTY_Identity-Opt']}
																	displayNameCode={[field.name, 'identityLabel']}
																	code="PROPERTY_Identity-Opt"
																	rules={[
																		{
																			required: true,
																			message: 'Identity is required',
																		},
																	]}
																	disabled={screen === 'VIEW' ? true : false}
																/>
															</TableCell>

															<TableCell>
																<Form.Item
																	noStyle
																	shouldUpdate={(prevValues, currentValues) =>
																		prevValues?.policyTable?.[index]?.['PROPERTY_Identity-Opt'] !==
																		currentValues?.policyTable?.[index]?.['PROPERTY_Identity-Opt']
																	}
																	rules={[
																		{
																			required: true,
																			message: 'Value is required',
																		},
																	]}
																>
																	{({ getFieldValue }) => (
																		<PolicyTableSelect
																			isDependent={true}
																			name={[field.name, 'identityValue']}
																			displayNameCode={[field.name, 'valueLabel']}
																			code={
																				queryNameMap?.[
																					getFieldValue(['policyTable', index, 'PROPERTY_Identity-Opt'])
																				] ?? null
																			}
																			rules={[
																				{
																					required: true,
																					message: 'Value is required',
																				},
																			]}
																			disabled={screen === 'VIEW' ? true : false}
																		/>
																	)}
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item
																	style={{ margin: 0 }}
																	name={[field.name, 'entitlementDays']}
																	rules={[
																		{
																			required: true,
																			message: 'Entitlement is required',
																		},
																	]}
																>
																	<Input placeholder="Write here" disabled={screen === 'VIEW' ? true : false} />
																</Form.Item>
															</TableCell>

															{/* --- MODIFIED: Recurrence Cell --- */}
															<TableCell>
																<Form.Item
																	name={[field.name, 'recurrence']}
																	style={{ margin: 0 }}
																	rules={[
																		{
																			required: true,
																			message: 'Recurrence is required',
																		},
																	]}
																>
																	{selectedCategory === 'regular' ? (
																		// Case 1: Regular Leave -> Select with only 'Annually'
																		<Select
																			allowClear
																			placeholder="Select recurrence"
																			style={{ width: '100%' }}
																			disabled={screen === 'VIEW' ? true : false}
																		>
																			<Select.Option value="annually">Annually</Select.Option>
																			<Select.Option value="monthly">Monthly</Select.Option>
																		</Select>
																	) : selectedCategory === 'special' ? (
																		// Case 2: Special Leave -> Number Input
																		<Input
																			type="number"
																			placeholder="Enter number value"
																			style={{ width: '100%' }}
																		/>
																	) : (
																		// Default Case (Governance, etc.) -> Original Select
																		<Select
																			allowClear
																			placeholder="Select recurrence"
																			style={{ width: '100%' }}
																			disabled={screen === 'VIEW' ? true : false}
																		>
																			<Select.Option value="annually">Annually</Select.Option>
																			<Select.Option value="monthly">Monthly</Select.Option>
																		</Select>
																	)}
																</Form.Item>
															</TableCell>
															{/* --- END MODIFICATION --- */}

															{screen !== 'VIEW' && (
																<TableCell align="center">
																	{fields.length === 1 && index === 0 && <AddIconButton onClick={() => add()} />}

																	{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																		<DeleteIconButton onClick={() => remove(field.name)} />
																	)}

																	{fields.length > 1 && index === fields.length - 1 && (
																		<>
																			<AddIconButton onClick={() => add()} />

																			<DeleteIconButton onClick={() => remove(field.name)} />
																		</>
																	)}

																	{fields.length > 1 && index === 0 && <span></span>}
																</TableCell>
															)}
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</Form.List>
							</Col>
							{screen !== 'VIEW' && (
								<Col xs={24}>
									<FormSubmitButton activate={isSubmitDisabled} details={false} />
								</Col>
							)}
						</Row>
					</Form>
				</SectionCard>
			</Spin>

			{showModal && (
				<ComptrollerConfirmModal
					showModal={showModal}
					modalData={{
						head: 'Leave Policy',
						label: 'Do you want to proceed?',
					}}
					setShowModal={setShowModal}
					handleSubmit={onModalOkHandler}
				/>
			)}
		</Box>
	);
}

export default Update;

export const PolicyTableSelect = ({ name, displayNameCode, code, rules, dependentCode, dp, isDependent = false, disabled }) => {
	const form = Form.useFormInstance();
	const userId = useSelector((state) => state.profile.profileData.userId);
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);
	const { state } = useLocation();

	const [options, setOptions] = useState(isDependent ? [] : null);

	const [GetLynFormData, { data: lynFormData, isLoading: isLynFormDataLoading }] = useGetLynFormDataMutation();
	const [GetLynFormProperty, { data: lynProperty, isLoading: isLynPropertyLoading }] = useGetLynFormPropertyMutation();

	const onSelectHandler = (value) => {
		if (displayNameCode) {
			const label = form.getFieldValue(value);
			form.setFieldValue(['policyTable', ...displayNameCode], label);
		}
	};

	const onChangeHandler = () => {
		if (dependentCode) {
			form.setFieldValue(dependentCode);
		}
	};

	useEffect(() => {
		if (code?.startsWith('PROPERTY_')) {
			GetLynFormProperty({
				type: 'FORM',
				fieldCode: state?.processCode || dp?.processCode,
				processVersion: state?.processVersion || dp?.processVersion,
				propertyCode: code,
				companyCode,
				status: 'Active',
			});
		} else if (code && userId) {
			GetLynFormData({
				name: code,
				userId,
			});
		}
	}, [code, userId]);

	useEffect(() => {
		if (lynProperty?.lynProperties?.length > 0) {
			setOptions(lynProperty?.lynProperties ?? []);
		}

		if (lynFormData?.results?.length > 0) {
			setOptions(lynFormData?.results ?? []);
		}
	}, [lynProperty, lynFormData]);

	let isLoading = isLynFormDataLoading || isLynPropertyLoading;

	return (
		<>
			<Form.Item name={name} rules={rules}>
				<Select
					allowClear
					style={{ width: '100%' }}
					showSearch
					filterOption={filterChildOptionLabel}
					onChange={onChangeHandler}
					onSelect={onSelectHandler}
					placeholder="Select from the list"
					loading={isLoading}
					disabled={disabled}
				>
					{options?.map((value, index) => (
						<Select.Option key={index} value={value?.propertyValue}>
							{value?.displayName}
						</Select.Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item noStyle name={displayNameCode} />
		</>
	);
};
